import {DatePicker, Input, Radio, TimePicker} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, FormikConsumer} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import {createControlledFormField} from '../../factory/createFormField'
import {Pure} from "../../../../components/Pure";
import filterOption from "../../../../helpers/filterOption";
import {renderElse} from "../../../Shared";
import {weekdays} from "../../../../conts/dates";
import {IoCalendarOutline} from "react-icons/io5";
import dayjs from "dayjs"
import customParseFormat from 'dayjs/plugin/customParseFormat';
import classNames from "classnames";
import {IoIosTimer} from "react-icons/io";

dayjs.extend(customParseFormat);

const specialEventOptions = [
    {
        value: 0,
        label: 'normal mass',
    },
    {
        value: 1,
        label: 'special mass',
    },
]
const massOptions = [
    {
        value: 1,
        label: 'sunday mass',
    },
    {
        value: 0,
        label: 'daily mass',
    },
]
export const BooleanPicker = ({
                                  name,
                                  value = false,
                                  onChange,
                                  defaultValue,
                                  disabled = false,
                                  options = specialEventOptions,
                              }) =>
    renderElse(
        _.isEmpty(options),
        <Translate>
            {(t) => (
                <Radio.Group
                    name={name}
                    value={value}
                    disabled={disabled}
                    buttonStyle="solid"
                    className="custom-radio-button"
                    defaultValue={defaultValue}
                    onChange={(event) => {
                        onChange(createValue(name, event.target.value))
                    }}>
                    {Array.from(options ?? []).map(({label, value}) => (
                        <React.Fragment key={value}>
                            <Radio.Button value={value}>{t(label)}</Radio.Button>
                        </React.Fragment>
                    ))}
                </Radio.Group>
            )}
        </Translate>
    )

const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    // rows: 2,
                    placeholder: 'title',
                    Component: Input,
                }),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 3,
                    placeholder: 'description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },
    language: {
        title: 'language',
        children: [
            {
                name: 'language',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <SelectWidget
                                name={name}
                                value={value}
                                allowClear={true}
                                className="w-full"
                                defaultValue="vi"
                                placeholder={t('language')}
                                onChange={(value) => {
                                    onChange(createValue(name, value ?? null))
                                }}
                                options={[
                                    {label: 'vi', value: 'vi'},
                                    {label: 'en', value: 'en'},
                                    {label: 'fr', value: 'fr'},
                                    {label: 'zh', value: 'zh'},
                                    {label: 'ko', value: 'ko'},
                                ].map(deepTranslate(t))}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    special_event: {
        title: 'mass',
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-2',
        children: [
            {
                name: 'special_event_flag',
                component: BooleanPicker,
            },
            {
                name: 'special_event_datetime',
                component: ({name, value, onChange, form, ...props}) => {
                    const special_event_flag = form?.values?.special_event_flag
                    return !!special_event_flag && (
                        <Pure input={[value]}>
                            <Translate>
                                {(t) => (
                                    <DatePicker
                                        showTime
                                        name={name}
                                        className="w-full"
                                        format={'HH:mm - DD/MM/YYYY'}
                                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                        placeholder={t('ex: 07:00 - 20/10/2010')}
                                        defaultValue={value ? dayjs(new Date(value)) : undefined}
                                        onChange={(date, dateString) => {
                                            onChange(
                                                createValue(
                                                    name,
                                                    date ? dayjs(new Date(date)).format() : undefined
                                                )
                                            )
                                        }}
                                        {...props}
                                    />
                                )}
                            </Translate>
                        </Pure>
                    )
                },
            }
        ],
    },
    scheduled_time: {
        title: 'mass times',
        inline: true,
        className: 'grid grid-cols-2 gap-2',
        children: [
            {
                name: 'day_of_week',
                component: ({name, value, onChange, form, ...props}) => {
                    const special_event_flag = form?.values?.special_event_flag

                    return !!!special_event_flag && (
                        <Pure input={[value]}>
                            <Translate>
                                {(t) => {
                                    const options = weekdays.map(({value, label}) => ({
                                        value: value,
                                        label: t(label)
                                    }));

                                    return (
                                        <SelectWidget
                                            value={value}
                                            className="w-full"
                                            showSearch={true}
                                            allowClear={true}
                                            options={options}
                                            filterOption={filterOption}
                                            onChange={(value, option) => {
                                                onChange(createValue(name, value))
                                            }}
                                        />
                                    )
                                }}
                            </Translate>
                        </Pure>
                    )
                },
            },
            {
                name: 'start_time',
                component: ({name, value, onChange, form, ...props}) => {
                    const special_event_flag = form?.values?.special_event_flag

                    return !!!special_event_flag && (
                        <Pure input={[value]}>
                            <Translate>
                                {(t) => (
                                    <TimePicker
                                        name={name}
                                        value={value ? dayjs(value, 'HH:mm') : undefined}
                                        className="w-full"
                                        format={'HH:mm'}
                                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                        placeholder={t('ex: 07:00')}
                                        // defaultValue={"07:00"}
                                        onChange={(time, timeString) => {
                                            onChange(
                                                createValue(
                                                    name,
                                                    timeString
                                                )
                                            )
                                        }}
                                        {...props}
                                    />
                                )}
                            </Translate>
                        </Pure>
                    )
                },
            }
        ],
    },
    scheduled_time2: {
        title: 'mass times',
        inline: true,
        className: 'flex items-center gap-2',
        itemClassNames: ['', 'flex-1'],
        children: [
            {
                name: 'weekdays',
                component: ({name, value, onChange, form, ...props}) => {
                    const special_event_flag = form?.values?.special_event_flag
                    const weekdays = form?.values?.weekdays ?? "0000000";
                    const weekdaysArray = Object.assign([], weekdays)

                    return !!!special_event_flag && (
                        <Pure input={[value]}>
                            <Translate>
                                {(t) => {

                                    return (
                                        <div className="grid grid-cols-7 gap-1 text-xs uppercase font-bold">
                                            {[
                                                {value: 'S', label: 'sun'},
                                                {value: 'M', label: 'mon'},
                                                {value: 'T', label: 'tue'},
                                                {value: 'W', label: 'wed'},
                                                {value: 'T', label: 'thu'},
                                                {value: 'F', label: 'fri'},
                                                {value: 'S', label: 'sat'},
                                            ].map(({label}, index) => (
                                                <div
                                                    key={index}
                                                    onClick={() => {
                                                        weekdaysArray[index] = weekdaysArray[index] === '0' ? '1' : '0';
                                                        onChange(
                                                            createValue('weekdays', weekdaysArray.join(''))
                                                        )
                                                    }}
                                                    style={{padding: '0.5rem'}}
                                                    className={classNames(
                                                        'flex flex-center rounded-md text-2xs text-primary cursor-pointer leading-tight',
                                                        !!Number(weekdaysArray[index])
                                                            ? 'bg-primary-100'
                                                            : 'background-200 text-color-100 hover:text-primary-600 hover:bg-primary-50'
                                                    )}>
                                                    {t(label)}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                }}
                            </Translate>
                        </Pure>
                    )
                },
            },
            {
                name: 'start_time',
                component: ({name, value, onChange, form, ...props}) => {
                    return (
                        <Translate>
                            {(t) => (
                                <TimePicker
                                    name={name}
                                    defaultValue={dayjs(value, 'HH:mm')}
                                    className="w-full"
                                    format={'HH:mm'}
                                    changeOnScroll
                                    needConfirm={false}
                                    suffixIcon={<IoIosTimer className="text-color-500"/>}
                                    placeholder={t('ex: 07:00')}
                                    onChange={(time, timeString) => {
                                        onChange(
                                            createValue(
                                                name,
                                                timeString
                                            )
                                        )
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            }
        ],
    },
    sunday_mass_flag: {
        title: 'sunday mass',
        inline: true,
        className: 'grid grid-cols-1 gap-2',
        children: [
            {
                name: 'sunday_mass_flag',
                component: ({...props}) => {
                    const special_event_flag = props?.form?.values?.special_event_flag

                    return !!!special_event_flag && (
                        <BooleanPicker options={massOptions} {...props} />
                    )
                },
            }
        ],
    }
}


export const CreateChurchMassTimeForm = ({
                                             onCancel = NullAsDefault,
                                             onSubmit = NullAsDefault,
                                         }) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}
        _.forEach(['title', 'language'], (name) => {
            const value = _.get(values, name)
            if (_.isEmpty(value ?? undefined)) {
                errors[name] = t('required field')
            }
        })
        return errors
    }

    return (
        <Formik
            initialValues={{
                language: 'vi',
                title: undefined,
                description: undefined,
                day_of_week: 0,
                start_time: '00:00',
                sunday_mass_flag: 0,
                special_event_flag: 0,
                special_event_datetime: undefined,
            }}
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => {
                onSubmit(_.omitBy(values, _.isUndefined))
            }}>
            <div className="flex flex-col gap-3">
                <FieldsFactory
                    formSchema={[
                        formSchema.title,
                        formSchema.description,
                        formSchema.language,
                        formSchema.special_event,
                    ]}
                />
                <FormikConsumer>
                    {({errors, values, handleChange}) => !!!values?.special_event_flag && (
                        <FieldsFactory
                            formSchema={[
                                formSchema.scheduled_time2,
                                formSchema.sunday_mass_flag,
                            ]}
                        />
                    )}
                </FormikConsumer>
                {/*<AdvancedFormFields*/}
                {/*  formSchema={[formSchema.description]}*/}
                {/*/>*/}
                <FormActionBar submitText="create"/>
            </div>
        </Formik>
    )
}

export const EditChurchMassTimeForm = ({
                                           item,
                                           onCancel = NullAsDefault,
                                           onSubmit = NullAsDefault,
                                       }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => ({...item}), [item])

    const validate = (values) => {
        let errors = {}
        _.forEach(['title', 'language'], (name) => {
            const value = _.get(values, name)
            if (_.isEmpty(value ?? undefined)) {
                errors[name] = t('required field')
            }
        })
        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const change = shallowDiff(values, initialValues)
                onSubmit(_.omitBy(change, _.isUndefined))
            }}>
            <div className="flex flex-col gap-3">
                <FieldsFactory
                    formSchema={[
                        formSchema.title,
                        formSchema.description,
                        formSchema.language,
                        formSchema.special_event,
                    ]}
                />
                <FormikConsumer>
                    {({errors, values, handleChange}) => !!!values?.special_event_flag && (
                        <FieldsFactory
                            formSchema={[
                                formSchema.scheduled_time2,
                                formSchema.sunday_mass_flag,
                            ]}
                        />
                    )}
                </FormikConsumer>
                <FormActionBar submitText="save"/>
            </div>
        </Formik>
    )
}

